import _ from 'lodash';
import { ActivityModel, ActivityView, GenericActivity } from '../Activities';
import { TBelongsTo } from '../_generics';
import {
  ActivityGroupModel,
  ActivityGroupView,
  OrderModel,
  OrderView,
  RoutingItemModel,
  RoutingItemStepModel,
  RoutingItemStepView,
  RoutingItemView,
} from '../OrderDocumentation';

function reduceToMap<T extends { key: string }>(entries: T[]): Record<string, T> {
  return entries.reduce(
    (result, entry) => {
      result[entry.key] = entry;
      return result;
    },
    {} as Record<string, T>,
  );
}

export function activityModelToViewFactory(genericActivities: Record<string, GenericActivity>) {
  return (activity: ActivityModel): ActivityView => {
    if (!(activity.genericActivityKey in genericActivities)) {
      throw new Error(`Generic activity ${activity.genericActivityKey} does not exists`);
    }
    const genericActivity = genericActivities[activity.genericActivityKey];
    return {
      ...activity,
      ...genericActivity(activity),
    };
  };
}

function constructRecordFromBelongsToFactory<
  TItemsKey extends string,
  TItemView extends TBelongsTo,
  TParentView extends { key: string } & { [T in TItemsKey]: Record<string, TItemView> },
>(items: TItemView[], itemsKey: TItemsKey) {
  return (parent: Omit<TParentView, TItemsKey>): TParentView => {
    const key = (parent as any).key;
    return {
      ...parent,
      [itemsKey]: reduceToMap(
        items.filter((item) => item.belongsToKey === key).sort((a, b) => a.belongsToPosition - b.belongsToPosition),
      ),
    } as TParentView;
  };
}

export function OrderModel2View(
  order: OrderModel,
  routingItems: RoutingItemModel[],
  routingItemSteps: RoutingItemStepModel[],
  activityGroups: ActivityGroupModel[],
  activities: ActivityModel[],
  genericActivities: Record<string, GenericActivity>,
): OrderView {
  const activityViews = activities.map(activityModelToViewFactory(genericActivities));
  const activityGroupViews = activityGroups.map(
    constructRecordFromBelongsToFactory<'activities', ActivityView, ActivityGroupView>(activityViews, 'activities'),
  );
  const routingItemStepViews = routingItemSteps.map(
    constructRecordFromBelongsToFactory<'activityGroups', ActivityGroupView, RoutingItemStepView>(
      activityGroupViews,
      'activityGroups',
    ),
  );
  const routingItemViews = routingItems.map(
    constructRecordFromBelongsToFactory<'steps', RoutingItemStepView, RoutingItemView>(routingItemStepViews, 'steps'),
  );
  const orderView = constructRecordFromBelongsToFactory<'routing', RoutingItemView, OrderView>(
    routingItemViews,
    'routing',
  )(order);

  return _.cloneDeep(orderView);
}
